import { defineNuxtPlugin, useNuxtData } from '#app';
import { nextTick, watch } from 'vue';
import {
    setRenewalInsightProvider,
    type RenewalInsight,
    type RenewalInsightKind,
} from '~~/codepresso-renewal/composables/useRenewalInsights';
import { useHomeService } from '~/composables/service/useHomeService';
import type { ArticleModel } from '~~/package/mapper/home/model/ArticleModel';

/**
 * renewal 콘텐츠의 "최근 소식 / 인사이트" 를 우리 뉴스 API 로 연결한다.
 *
 * renewal 은 단독으로 뜰 때 자체 서버 라우트(`/api/insights`)를 쓰는데, 그 라우트는
 * 우리 앱에 없다. 그대로 두면 목록이 조용히 비거나 깨진다(실제로 빈 카드가 그려졌다).
 * 마크업은 renewal 이 갖고 데이터만 우리가 준다 — renewal 의 DOM·CSS 는 건드리지 않는다.
 *
 * 어떤 종류를 섞을지는 부르는 쪽이 정한다 — GNB 의 "최근 소식" 은 뉴스와 이벤트를
 * 함께(기본 항목이 뉴스 2 + 이벤트 1 구성이었다), 홈 하단 "최근 인사이트" 는 뉴스만.
 */

/** renewal `useRenewalInsights` 가 쓰는 useAsyncData 키의 접두사. */
export const RENEWAL_INSIGHT_KEY_PREFIX = 'renewal-insights-';

/** renewal `useRenewalInsights` 가 쓰는 useAsyncData 키. 형식은 renewal 이 정한다 — 어긋나면 테스트가 잡는다. */
export const insightKey = (limit: number, kinds: readonly RenewalInsightKind[]): string =>
    `${RENEWAL_INSIGHT_KEY_PREFIX}${limit}-${[...kinds].sort().join('+')}`;

/** 우리가 넘기는 인사이트. 어느 언어로 받아 온 목록인지 같이 싣는다. */
export type LocalizedRenewalInsight = RenewalInsight & { locale: string };

/** renewal 의 종류 이름을 우리 기사 API 의 label 로 바꾼다. */
export const articleTypesFor = (kinds: readonly RenewalInsightKind[]): string[] =>
    kinds.map((kind) => (kind === 'news' ? 'NEWS' : 'EVENT'));

/** 뱃지에 쓰는 짧은 분류 이름.
 *
 *  `package/locales` 에 넣지 않은 이유: 그 파일들은 번역 스프레드시트에서 통째로
 *  생성되므로(`npm run generate:locales`) 손으로 넣은 키는 다음 동기화에 사라진다.
 *  스프레드시트에 항목이 추가되면 그때 여기를 t() 호출로 바꾸면 된다. */
const CATEGORY_LABEL: Record<string, Record<string, string>> = {
    NEWS: { ko: '뉴스', en: 'News', ja: 'ニュース', vi: 'Tin tức', es: 'Noticias' },
    EVENT: { ko: '이벤트', en: 'Events', ja: 'イベント', vi: 'Sự kiện', es: 'Eventos' },
};

const categoryLabel = (label: string, locale: string): string =>
    CATEGORY_LABEL[label]?.[locale] ?? CATEGORY_LABEL[label]?.en ?? label;

export const toInsight = (article: ArticleModel, locale: string): LocalizedRenewalInsight => ({
    url: article.link,
    title: article.title,
    category: categoryLabel(article.label, locale),
    date: article.date,
    image: article.imageUrl,
    locale,
});

/**
 * 이미 이 언어로 받은 목록인가 — 항목이 있고 모두 이 언어 표시를 달고 있다.
 *
 * 언어 표시가 없는 항목(표시를 달기 전 배포의 payload)은 아니라고 본다.
 * 빈 목록도 아니라고 본다 — 프리렌더 때 기사 API 가 실패해 빈 목록이 구워졌을 수 있다.
 */
export const isInsightListInLocale = (list: unknown, locale: string): boolean =>
    Array.isArray(list) && list.length > 0
    && list.every((item) => (item as Partial<LocalizedRenewalInsight> | null)?.locale === locale);

/** insightKey 의 반대 — renewal 의 키 형식이 아니면 null. */
const parseInsightKey = (key: string): { limit: number; kinds: RenewalInsightKind[] } | null => {
    const match = new RegExp(`^${RENEWAL_INSIGHT_KEY_PREFIX}(\\d+)-([a-z+]+)$`).exec(key);
    if (!match) return null;
    const kinds = match[2].split('+');
    if (!kinds.every((kind) => kind === 'news' || kind === 'event')) return null;
    return { limit: Number(match[1]), kinds: kinds as RenewalInsightKind[] };
};

export default defineNuxtPlugin((nuxtApp) => {
    const homeService = useHomeService();
    const currentLocale = () => String((nuxtApp.$i18n as { locale?: { value?: string } })?.locale?.value ?? 'en');

    /** 기사 API 에서 목록을 받아 그 언어 표시를 단다. complete 는 요청이 모두 성공했는지다. */
    const fetchInsights = async (limit: number, kinds: readonly RenewalInsightKind[], locale: string) => {
        // 한쪽이 비거나 실패해도 나머지는 보여 준다.
        const pages = await Promise.allSettled(
            articleTypesFor(kinds).map((type) => homeService.getArticleList(1, limit, type, locale.toUpperCase())),
        );

        for (const result of pages) {
            // allSettled 는 실패를 삼킨다. 목록이 조용히 비면 원인을 못 찾으므로 남긴다.
            if (result.status === 'rejected') console.error('[renewal] 기사 목록 조회 실패', result.reason);
        }

        const insights = pages
            .flatMap((result) => (result.status === 'fulfilled' ? result.value.data : []))
            .filter((article) => article?.link && article?.title)
            .sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''))
            .slice(0, limit)
            .map((article) => toInsight(article, locale));

        return { insights, complete: pages.every((result) => result.status === 'fulfilled') };
    };

    /** 이 브라우저가 그 언어로 받았는데 기사가 없던 목록(키 → 언어). 빈 목록을 이동할 때마다 다시
     *  부르지 않게 한다. 요청이 하나라도 실패했으면 남기지 않는다 — 다음 기회에 다시 받는다. */
    const emptyIn = new Map<string, string>();

    setRenewalInsightProvider(async (limit, kinds) => {
        const locale = currentLocale();
        const { insights, complete } = await fetchInsights(limit, kinds, locale);
        if (import.meta.client && complete && insights.length === 0) emptyIn.set(insightKey(limit, kinds), locale);
        return insights;
    });

    if (import.meta.client) {
        // 정적 프리렌더는 기본 언어(en)로 구워지고, 그때 받은 목록이 payload 로 실려 온다.
        // renewal 의 useAsyncData 키에는 언어가 없어 클라이언트는 그 목록을 그대로 쓴다 —
        // 첫 로드의 hydration 도, SPA 이동 때 받는 `_payload.js` 도 마찬가지라 KO 방문자에게
        // 영문 기사가 남았다(TSK-12197). 언어 전환(푸터)도 새로고침 없이 setLocale 만 하므로
        // 목록이 이전 언어로 남는다.
        // 화면이 다 그려질 때마다, 그리고 언어가 바뀔 때 화면의 목록이 지금 언어가 아니면 다시 받는다.
        // EN 방문자는 프리렌더 목록이 이미 en 이라 추가 요청이 없다.
        //
        // useAsyncData 의 refresh(refreshNuxtData)를 쓰지 않고 직접 받아, 성공했을 때만 화면 데이터에 쓴다.
        // refresh 가 도는 동안 같은 키를 쓰는 페이지로 SPA 이동하면, Nuxt(3.15)는 새 페이지의 첫 조회를
        // 캐시(그 페이지의 `_payload.js`)로 끝내면서 진행 중이던 promise 를 취소만 하고 바꿔 끼우지 않는다.
        // 새 페이지의 `await useAsyncData` 는 그 취소된 promise 를 기다리는데, 그 promise 는 자기 자신으로
        // 풀리려 해 끝나지 않는다 — 이동이 멈추고, 같은 키를 쓰는 다음 이동도 모두 멈췄다(정적 빌드).
        //
        // 다시 받기를 기다리지 않는다(promise 를 돌려주지 않는다) — 기사 API 응답까지 page:finish
        // (스크롤 복원·로딩 종료)를 붙잡지 않게.
        const inFlight = new Set<string>();

        // 목록이 바뀌면 SiteNav 가 메뉴 DOM 을 통째로 다시 그려(v-html), 열려 있던 메뉴가 닫힌 새 DOM 으로 바뀐다.
        // renewal nav-menu.js 는 다시 묶을 때 헤더의 `is-nav-open` 을 맞추지 않아, 패널 없이 헤더만 흰 면으로
        // 남았다(다크 페이지에서 눈에 띈다). 다시 그려진 뒤 nav-menu.js 의 syncHeader 와 같은 규칙으로 맞춘다.
        const resyncNavHeader = () => nextTick(() => {
            const header = document.querySelector('[data-nav-header]');
            if (header?.classList.contains('is-nav-open') && !document.querySelector('[data-nav-item].is-open')) {
                header.classList.remove('is-nav-open');
            }
        });

        const refreshInsight = async (key: string, limit: number, kinds: RenewalInsightKind[], locale: string) => {
            const flight = `${key}|${locale}`;
            if (inFlight.has(flight)) return;
            inFlight.add(flight);
            try {
                const { insights, complete } = await fetchInsights(limit, kinds, locale);
                // 실패가 섞였으면 보이는 목록을 그대로 둔다 — 다음 page:finish·언어 전환 때 다시 받는다.
                // 받는 사이 언어가 바뀌었으면 버린다 — 바뀐 언어로 받는 요청이 따로 돈다.
                if (!complete || currentLocale() !== locale) return;
                nuxtApp.runWithContext(() => {
                    useNuxtData(key).data.value = insights;
                });
                void resyncNavHeader();
                if (insights.length === 0) emptyIn.set(key, locale);
            } finally {
                inFlight.delete(flight);
            }
        };

        const refreshStaleInsights = (): void => {
            const locale = currentLocale();
            const keys = new Set([...Object.keys(nuxtApp.payload.data), ...Object.keys(nuxtApp.static.data)]);
            for (const key of keys) {
                const target = parseInsightKey(key);
                if (!target) continue;
                const shown = nuxtApp.runWithContext(() => useNuxtData(key).data.value);
                if (isInsightListInLocale(shown, locale)) continue;
                if (Array.isArray(shown) && shown.length === 0 && emptyIn.get(key) === locale) continue;
                void refreshInsight(key, target.limit, target.kinds, locale);
            }
        };

        nuxtApp.hook('page:finish', refreshStaleInsights);
        watch(currentLocale, refreshStaleInsights);
    }
});
